import {
  fetchBasicSystemConfiguration,
  fetchCurrentTransformers,
  fetchPreSelectedPowerMeter,
  fetchRetrofitBattery,
  fetchShopPowerMeters,
  fetchSystemConfigurationComponents,
  fetchSingleSystemConfiguration,
} from '../api/shop-system-configuration.api'
import {
  BasicSystemResponseModel,
  Element,
  PowerMeterModalModelResponse,
  PowerMeterElements,
  RetrofitBatteryModalResponse,
  SystemConfigurationComponentsResponse,
  TransformerModalModelResponse,
} from '../models'
import { useShopFetchWithResult } from './use-shop-fetch'

/*
 * Theese Custom Hook have all System Configurator Endpoints.
 * Everything Related to System Configurator, like Power Meters, Main or Additional Components, Basic Component, Transformers and Retrofit Batteries
 */

interface UseShopSystemConfigurationHook {
  getBasicSystemConfiguration: () => Promise<BasicSystemResponseModel | null>
  getSystemConfigurationComponents: (configuratorId: string) => Promise<SystemConfigurationComponentsResponse | null>
  getShopPowerMeters: (powerMeterIds: string[]) => Promise<Element[] | null>
  getPreSelectedPowerMeter: (powerMeterId: string) => Promise<PowerMeterModalModelResponse | null>
  getCurrentTransformers: () => Promise<TransformerModalModelResponse | null>
  getRetrofitBattery: (batteryId: string) => Promise<RetrofitBatteryModalResponse | null>
  getSingleSystemConfiguration: (configuratorId: string) => Promise<BasicSystemResponseModel | null>
}

const useShopSystemConfiguration = (): UseShopSystemConfigurationHook => {
  const shopFetch = useShopFetchWithResult()

  const getBasicSystemConfiguration = async (): Promise<BasicSystemResponseModel | null> => {
    return (await fetchBasicSystemConfiguration(shopFetch)).value
  }

  const getSystemConfigurationComponents = async (configuratorId: string): Promise<SystemConfigurationComponentsResponse | null> => {
    return (await fetchSystemConfigurationComponents(configuratorId, shopFetch)).value
  }

  const getShopPowerMeters = async (powerMeterIds: string[]): Promise<Element[]> => {
    const result = await fetchShopPowerMeters(powerMeterIds, shopFetch)
    if (result.value !== null) {
      const uniqueProducts = new Set<string>(powerMeterIds)
      const duplicatedResponse: Element[] = powerMeterIds.flatMap(id => {
        if (uniqueProducts.has(id)) {
          uniqueProducts.delete(id)
        }
        return (result.value as PowerMeterElements).elements.filter((item: { id: string }) => item.id === id)
      })
      return duplicatedResponse
    }
    return []
  }

  const getPreSelectedPowerMeter = async (powerMeterIds: string): Promise<PowerMeterModalModelResponse | null> => {
    return (await fetchPreSelectedPowerMeter(powerMeterIds, shopFetch)).value
  }

  const getCurrentTransformers = async (): Promise<TransformerModalModelResponse | null> => {
    return (await fetchCurrentTransformers(shopFetch)).value
  }

  const getRetrofitBattery = async (batteryId: string): Promise<RetrofitBatteryModalResponse | null> => {
    return (await fetchRetrofitBattery(batteryId, shopFetch)).value
  }

  const getSingleSystemConfiguration = async (configuratorId: string): Promise<BasicSystemResponseModel | null> => {
    return (await fetchSingleSystemConfiguration(configuratorId, shopFetch)).value
  }

  return {
    getBasicSystemConfiguration,
    getSystemConfigurationComponents,
    getShopPowerMeters,
    getPreSelectedPowerMeter,
    getCurrentTransformers,
    getRetrofitBattery,
    getSingleSystemConfiguration,
  }
}

export default useShopSystemConfiguration
